import React from "react";
import SEO from "../components/SEO";
import { PageHero, SectionHeading, FeatureCard, StatCard, CTABand, CheckList, Icon } from "../components/ui-blocks";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { dental, images, company } from "../mock/mock";

export default function DentalOrthodontics() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Dental & Orthodontic Insurance Verification and Billing Support",
    provider: { "@type": "Organization", name: company.legal },
    areaServed: "US",
    description:
      "Remote insurance verification, claims, billing and admin support for dental and orthodontic practices.",
  };
  return (
    <>
      <SEO
        title="Dental & Orthodontic Insurance Verification, Billing & Admin Support"
        description="Help your orthodontic or dental practice save time and money on insurance verification, claims, billing and front-desk admin. Reduce denials, protect case acceptance and get paid faster — no added payroll."
        path="/dental-orthodontics"
        jsonLd={jsonLd}
      />
      <PageHero eyebrow={dental.hero.eyebrow} title={dental.hero.title} subtitle={dental.hero.subtitle} image={images[dental.hero.image]}>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button asChild size="lg" className="bg-brand-600 hover:bg-brand-700"><Link to="/contact-us">Get a Free Revenue Risk Audit <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
      </PageHero>

      {/* SAVINGS */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="Real savings" title={dental.savings.title} subtitle="Structured remote support that pays for itself in recovered time and revenue." />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {dental.savings.items.map((s) => <StatCard key={s.label} value={s.value} label={s.label} icon={s.icon} />)}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center eyebrow="The hidden leak" title={dental.problem.title} subtitle={dental.problem.body} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {dental.problem.pains.map((p) => (
              <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500"><Icon name={p.icon} className="h-5 w-5" /></div>
                <h3 className="mt-4 text-base font-semibold text-brand-navy">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="What we handle" title={dental.services.title} subtitle="Everything insurance-related, managed as an extension of your team." />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {dental.services.items.map((s) => <FeatureCard key={s.title} icon={s.icon} title={s.title} body={s.body} />)}
        </div>
      </section>

      {/* ORTHO SPECIFIC */}
      <section className="bg-brand-navy">
        <div className="container-x py-16 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
              <img src={images[dental.ortho.image]} alt="Orthodontic examination — braces treatment planning" className="h-[420px] w-full object-cover" />
            </div>
            <div>
              <SectionHeading light eyebrow="Orthodontics" title={dental.ortho.title} />
              <p className="mt-5 text-brand-100/90 leading-relaxed">{dental.ortho.body}</p>
              <div className="mt-6"><CheckList items={dental.ortho.bullets} light /></div>
            </div>
          </div>
        </div>
      </section>

      <CTABand title={dental.cta.title} body={dental.cta.body} secondaryTo="/insurance-verification" secondaryLabel="How verification works" />
    </>
  );
}
